import { useRequest } from 'ahooks'
import { Button, Form, Modal, Switch } from 'antd'
import { saveAs } from 'file-saver'
import { useAtom } from 'jotai'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { userInfoAtom } from '../user'

export const CustomerHomeExport = ({ selfIds }: { selfIds: string[] }) => {
  const { t } = useTranslation()
  const [viewer] = useAtom(userInfoAtom)
  const [visible, setVisible] = React.useState(false)
  const [form] = Form.useForm()

  const { data: customerExportRoles } = useRequest('/api/customer/export/roles')

  const { run: exportCustomer, loading: exportLoading } = useRequest(
    (data) => ({
      url: '/api/customer/export/excel',
      method: 'POST',
      data,
      responseType: 'blob',
    }),
    {
      manual: true,
      onSuccess: (res) => {
        const date = moment().utcOffset(8).format('YYYYMMDD_HHmmss')
        saveAs(res, `customer_${date}.xlsx`)
      },
    }
  )

  const roles = customerExportRoles || []
  const canExport = viewer.isSuperAdmin || viewer.isMiniIPDCore || roles.includes(viewer.email)

  if (!canExport) {
    return null
  }

  return (
    <>
      <Button type='link' loading={exportLoading} onClick={() => setVisible(true)}>
        {t('export customer')}
      </Button>
      <Modal
        title={t('export customer')}
        visible={visible}
        onOk={() => {
          const fields = form.getFieldsValue()
          exportCustomer({ selfIds, fields })
          setVisible(false)
        }}
        onCancel={() => {
          setVisible(false)
        }}
        okButtonProps={{ loading: exportLoading }}
        cancelButtonProps={{ disabled: exportLoading }}
      >
        <Form
          form={form}
          initialValues={{
            selfId: true,
            email: true,
            serviceOwner: true,
            name: true,
            region: true,
            TO: true,
            flowStatus: true,
            priority: true,
            businessGoal: true,
            endDate: true,
            businessTargetCompletePercentage: true,
            resultTrackingCompleteTime: true,
            resultTrackingComment: true,
            deliveryTargetCompleteTime: true,
            actualCompleteTime: true,
            actualWorkload: true,
            projectManager: true,
          }}
        >
          <Form.Item name='selfId' label={t('index')} valuePropName='checked'>
            <Switch disabled />
          </Form.Item>
          <Form.Item name='email' label={t('customer.detail.email')} valuePropName='checked'>
            <Switch disabled />
          </Form.Item>
          <Form.Item name='serviceOwner' label={t('customer.serviceOwner')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='name' label={t('customer.name')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='region' label={t('customer.region')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='TO' label={t('customer.TO')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='flowStatus' label={t('customer.status.title')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='priority' label={t('customer.detail.priority')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='businessGoal' label={t('customer.detail.businessGoal')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='endDate' label={t('customer.detail.endDate')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item
            name='businessTargetCompletePercentage'
            label={t('customer.export.businessTargetCompletePercentage')}
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name='resultTrackingCompleteTime'
            label={t('customer.export.ResultTrackingCompleteTime')}
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name='resultTrackingComment'
            label={t('customer.flowStatus.ResultTracking.comment')}
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name='deliveryTargetCompleteTime'
            label={t('customer.export.deliveryTargetCompleteTime')}
            valuePropName='checked'
          >
            <Switch />
          </Form.Item>
          <Form.Item name='actualCompleteTime' label={t('customer.export.actualCompleteTime')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='actualWorkload' label={t('customer.actualWorkload.title')} valuePropName='checked'>
            <Switch />
          </Form.Item>
          <Form.Item name='projectManager' label={t('customer.projectManager.title')} valuePropName='checked'>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
